/* CUSTOM_PRODUCTRULES_BY_CLAUDE (Ryan 2026-10-04, Product Rules Session 1) - Product Rules page.
   Planner: Team-Portal-Bot/tools/Product-Rules/CUSTOM-PRODUCT-RULES-PLANNER.md.

   One call: GET /product-rules (rfq-worker, admin only) returns every product with its own rule
   cells, every supplier with its supplier-level cells, the registry (areas, rules, gap checks) and
   the last build. This page joins them: a product's value for a rule is its own cell, else its
   supplier's - the same order the worker's agent read uses (mergedRules).

   READ ONLY in Session 1. The dense table is Ryan's pick (2026-10-04): one row per product, a column
   per rule of the chosen area, a source badge in every cell; a row opens to every rule, the gaps,
   where each rule is edited, and the variants (GET /product-rules/product). */
(function () {
  'use strict';

  var PAGE_SIZE = 100;
  var SOURCE = {
    supplier: { short: 'S', title: 'Supplier: set in the Vendor Guide' },
    product: { short: 'P', title: 'Product: set on this product' },
    variant: { short: 'V', title: 'Variant: set per variant' },
    inferred: { short: 'inf', title: 'Inferred: derived, nobody typed it' },
    setting: { short: 'set', title: 'Setting: a portal setting' },
    code: { short: 'code', title: 'Hard-coded in the worker' },
    none: { short: '', title: 'Nothing on file' },
  };

  // ── pure helpers (exposed for tests) ────────────────────────────────────────────────────────

  function norm(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }

  /** A product's value for a rule: its own cell, else its supplier's. */
  function cellFor(product, suppliers, ruleId) {
    var own = product && product.cells ? product.cells[ruleId] : null;
    if (own) return own;
    var sup = suppliers && product ? suppliers[product.v] : null;
    return sup && sup.cells ? (sup.cells[ruleId] || null) : null;
  }

  function searchKey(p) { return norm([p.t, p.h, p.v].join(' ')); }

  /** Filter the product list. f = {q, supplier, status, gap}. */
  function filterRows(products, keys, f) {
    var words = norm(f.q).split(' ').filter(Boolean);
    var out = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (f.status && f.status !== 'all' && p.st !== f.status) continue;
      if (f.supplier && p.v !== f.supplier) continue;
      if (f.vendorSet && !f.vendorSet[p.v]) continue;
      if (f.gap === 'any-red') { if (!f.redIds || !p.gaps.some(function (g) { return f.redIds[g]; })) continue; }
      else if (f.gap && p.gaps.indexOf(f.gap) === -1) continue;
      var ok = true;
      for (var w = 0; w < words.length; w++) { if (keys[i].indexOf(words[w]) === -1) { ok = false; break; } }
      if (ok) out.push(p);
    }
    return out;
  }

  /** Count each gap over a set of products. */
  function gapCounts(products) {
    var c = {};
    for (var i = 0; i < products.length; i++) {
      var g = products[i].gaps || [];
      for (var j = 0; j < g.length; j++) c[g[j]] = (c[g[j]] || 0) + 1;
    }
    return c;
  }

  function ageText(ms, now) {
    if (!ms) return 'never';
    var h = ((now || Date.now()) - ms) / 3600000;
    if (h < 1) return Math.max(1, Math.round(h * 60)) + ' min ago';
    if (h < 48) return Math.round(h) + 'h ago';
    return Math.round(h / 24) + ' days ago';
  }

  function parseHash(hash) {
    var m = String(hash || '').match(/[#&]p=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /* Session 2: `#g=<Guide vendor id>` is the Vendor Guide's "see these products" link. */
  function parseGuideHash(hash) {
    var m = String(hash || '').match(/[#&]g=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /** The Shopify vendor names whose supplier resolves to one Guide record. */
  function vendorsForGuide(suppliers, guideId) {
    var out = {};
    Object.keys(suppliers || {}).forEach(function (n) { if (guideId && suppliers[n].guideId === guideId) out[n] = true; });
    return out;
  }

  /** The value an editor opens on, read from what the page already holds. Pure. */
  function editValue(rule, guide, cell) {
    var po = (guide && guide.po) || {};
    var raw = (cell && cell.raw) || {};
    switch (rule) {
      case 'shipping-model': return { model: po.shippingModel || 'standard', flatRate: po.shippingFlatRate || '' };
      case 'freight-procedure': return { mode: po.freightMode || '', basis: po.lockedRateBasis || '', rate: po.lockedRate || '' };
      case 'po-workflow': return { value: po.workflow || '' };
      case 'post-po-wait': return { value: po.postPoWait || 'none' };
      case 'lead-time': return { value: (guide && guide.leadTime) || '' };
      case 'pallet-fee': return { fee: po.palletFee || 0, label: po.palletFeeLabel || '' };
      case 'pallet-fee-exclusion': return { excluded: !!raw.excluded };
      case 'product-fee': return { amount: raw.own ? raw.amount : '', label: raw.own ? (raw.label || '') : '' }; // CUSTOM_SUPPLIERFEE_BY_CLAUDE
      case 'locked-rate': return { rate: raw.rate || '', pallet: raw.pallet || null };
      case 'po-send': return { allowed: !!(cell && String(cell.v).indexOf('yes') === 0) };
      /* Session 7 (Ryan 2026-10-10): the two rules that were code, now stored settings. */
      case 'post-ship-invoice': return { on: !!(cell && String(cell.v).indexOf('yes') === 0) };
      case 'west-coast-source': return { value: ((state.data.ruleSettings && state.data.ruleSettings.ecoreWestStates) || []).join(', ') };
      default: {
        var t = TRI[rule];
        if (!t) return {};
        var cur = po[t.field];
        return { value: cur === t.yes ? 'yes' : (cur === t.no ? 'no' : 'unset') };
      }
    }
  }

  /* Session 3 (Ryan 2026-10-07): what a product / variant editor opens on, read from the page's own
     copy of the product (fq, freeOk, uom, mf) and, for a variant rule, the variant row. Pure. */
  function productEditValue(rule, p, v) {
    var mf = (p && p.mf) || {};
    var vv = v || {};
    var n = function (x) { return x == null || x === '' ? '' : Number(x); };
    switch (rule) {
      case 'freight-quoted': return { on: !!(p && p.fq) };
      case 'ships-free-ok': return { on: !!(p && p.freeOk) };
      case 'ships-from-stock': return { stock: p && p.stockChase ? 'chase' : (p && p.fromStock ? 'in-stock' : '') }; // CUSTOM_USUALLYINSTOCK_BY_CLAUDE (ONYX 63612a1c4a15)
      case 'unit-of-measure': return { unit: (p && p.uom) || '' };
      case 'roll': return { width: n(mf.rollWidth), min: n(mf.minRoll), max: n(mf.maxRoll) };
      case 'po-unit': return { uom: mf.poUom || '', factor: n(mf.poUomFactor) };
      case 'carton': return { value: n(mf.qtyStep) };
      case 'variant-po-unit': return { uom: vv.poUom || '', factor: n(vv.poUomFactor) };
      case 'moq': return { value: n(vv.moq) };
      case 'supplier-sku': return { value: vv.supplierSku || '' };
      case 'bundle-skus': return { plunge: vv.bundlePlungeSku || '', chiller: vv.bundleChillerSku || '' };
      case 'variant-weight': return { lb: n(vv.weightLb) };
      default: return {};
    }
  }

  /** The ticked products, in table order, as handles. Pure. */
  function tickedHandles(products, ticked) {
    var out = [];
    for (var i = 0; i < products.length; i++) if (ticked[products[i].h]) out.push(products[i].h);
    return out;
  }

  var TRI = {
    'card-on-file': { field: 'settleMode', yes: 'vendor_charges_cc_on_file', no: 'manual', words: ['Yes, they charge our card on file', 'No, a person settles the bill'] },
    'vendor-billed-freight': { field: 'freightBilling', yes: 'vendor_bills_us', no: 'broker', words: ['Yes, on their invoice', 'No, we arrange the freight'] },
    'estimate-cost': { field: 'costsAreEstimates', yes: true, no: false, words: ['Yes, true up from their quote', 'No, the price list is the cost'] },
  };

  // ── DOM ─────────────────────────────────────────────────────────────────────────────────────

  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (!Object.prototype.hasOwnProperty.call(attrs, k) || attrs[k] == null) continue;
      if (k === 'text') n.textContent = attrs[k];
      else if (k === 'class') n.className = attrs[k];
      else if (k.indexOf('on') === 0) n.addEventListener(k.slice(2), attrs[k]);
      else n.setAttribute(k, attrs[k]);
    }
    (kids || []).forEach(function (c) { if (c) n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }
  function clear(n) { while (n && n.firstChild) n.removeChild(n.firstChild); }
  function api() { return window.RepPortal && window.RepPortal.API; }

  var state = {
    data: null, keys: [], redIds: {}, gapById: {}, rulesByArea: {},
    q: '', supplier: '', status: 'active', area: 'freight', gap: '',
    shown: PAGE_SIZE, open: null, detail: {},
    guideFilter: null, editing: null, logs: {}, flash: null, // Session 2
    cb: null, ovEdit: null, // Session 4: Catalog-Bot switch + proposals, the override editor
    ticked: {}, bulk: null, jobs: null, jobsOpen: false, lastRows: [], // Session 3
    acc: null, accOpen: false, // CUSTOM_FREIGHTACCURACY_BY_CLAUDE
    brands: null, brandOpen: null, brandDetail: null, // Session 8: the brand checklist
    view: 'products', checks: null, // Session 9: the Brands view; the Products view's checklist columns
  };
  /* Session 9: the "Brand checklist" choice in the Columns menu (one column per brand checklist rule). */
  var CHECKLIST_AREA = '__checklist';
  var root, listEl, chipsEl, countEl, bulkEl;

  function notice(kind, title, lead, extra) {
    return el('div', { class: 'cpr-notice cpr-notice--' + kind, role: kind === 'error' ? 'alert' : null }, [
      el('p', { class: 'cpr-notice__title', text: title }),
      lead ? el('p', { class: 'cpr-notice__lead', text: lead }) : null,
      extra || null,
    ]);
  }

  function load() {
    clear(root);
    root.appendChild(notice('loading', 'Loading every product and its rules…'));
    api().get('/product-rules').then(function (data) {
      if (!data || data.v !== 1 || !Array.isArray(data.products) || !data.registry) {
        clear(root);
        root.appendChild(notice('error', 'This page needs an update', 'The rules arrived in a format this page does not understand. Reload the page; if it persists, tell Ryan.'));
        return;
      }
      state.data = data;
      state.keys = data.products.map(searchKey);
      state.redIds = {}; state.gapById = {}; state.rulesByArea = {};
      data.registry.gaps.forEach(function (g) { state.gapById[g.id] = g; if (g.level === 'red') state.redIds[g.id] = true; });
      data.registry.rules.forEach(function (r) { (state.rulesByArea[r.area] = state.rulesByArea[r.area] || []).push(r); });
      if (!state.open) state.open = parseHash(location.hash);
      if (state.guideFilter === null) state.guideFilter = parseGuideHash(location.hash) || '';
      if (!state.viewRead) { state.viewRead = true; state.view = parseViewHash(location.hash); }
      state.detail = {};
      renderShell();
    }).catch(function (err) {
      clear(root);
      var retry = el('button', { class: 'cpr-btn', type: 'button', text: 'Try again', onclick: load });
      if (err && err.status === 503 && err.data && err.data.error === 'not-built') {
        var build = el('button', { class: 'cpr-btn', type: 'button', text: 'Build it now', onclick: rebuild });
        root.appendChild(notice('error', 'The index has not been built yet', 'It builds itself every night. Build it now (about a minute).', el('div', { class: 'cpr-row' }, [build, retry])));
      } else if (err && err.status === 403) {
        root.appendChild(notice('error', 'Admins only', 'Product Rules carries costs and supplier rates, so only admins can open it.'));
      } else {
        root.appendChild(notice('error', 'The rules could not load', (err && err.message ? err.message + '. ' : '') + 'This is a loading problem, not an empty catalog.', retry));
      }
    });
  }

  function rebuild() {
    clear(root);
    root.appendChild(notice('loading', 'Rebuilding the index from Shopify (about a minute)…'));
    api().post('/product-rules/rebuild', {}).then(load).catch(function (err) {
      clear(root);
      root.appendChild(notice('error', 'The rebuild failed', (err && err.data && err.data.error) || (err && err.message) || 'Unknown error', el('button', { class: 'cpr-btn', type: 'button', text: 'Back', onclick: load })));
    });
  }

  function select(label, value, options, onchange) {
    var s = el('select', { class: 'cpr-select', 'aria-label': label, onchange: function () { onchange(s.value); } },
      options.map(function (o) { var opt = el('option', { value: o[0], text: o[1] }); if (o[0] === value) opt.selected = true; return opt; }));
    return el('label', { class: 'cpr-field' }, [el('span', { class: 'cpr-field__label', text: label }), s]);
  }

  function renderShell() {
    if (state.view === 'brands') { renderBrandsView(); return; }
    clear(root);
    var d = state.data;
    var suppliers = Object.keys(d.suppliers).sort();
    var search = el('input', { class: 'cpr-search', type: 'search', placeholder: 'Search title, handle or supplier', value: state.q, 'aria-label': 'Search products' });
    search.addEventListener('input', function () { state.q = search.value; state.shown = PAGE_SIZE; renderList(); });
    var built = d.lastBuild && d.lastBuild.ok === false
      ? el('span', { class: 'cpr-meta cpr-meta--bad', text: 'Last nightly build failed: ' + d.lastBuild.error })
      : el('span', { class: 'cpr-meta', text: 'Index built ' + ageText(d.builtAt) + (d.dirty && d.dirty.applied ? ' · ' + d.dirty.applied + ' updated just now' : '')
          /* CUSTOM_FREIGHTLEARN_BY_CLAUDE (ONYX a7ae89d49096) - when shipment readings last reached the freight data. */
          + (d.freightLearning ? ' · Freight data last received ' + (d.freightLearning.lastIngestAt ? ageText(d.freightLearning.lastIngestAt) : 'never') : '') });
    var controls = el('div', { class: 'cpr-controls' }, [
      el('div', { class: 'cpr-field cpr-field--grow' }, [el('span', { class: 'cpr-field__label', text: 'Search' }), search]),
      select('Supplier', state.supplier, [['', 'All suppliers']].concat(suppliers.map(function (s) { return [s, s]; })), function (v) { state.supplier = v; state.shown = PAGE_SIZE; renderList(); }),
      select('Status', state.status, [['active', 'Active'], ['draft', 'Draft'], ['archived', 'Archived'], ['all', 'All statuses']], function (v) { state.status = v; state.shown = PAGE_SIZE; renderList(); }),
      select('Columns', state.area, d.registry.areas.map(function (a) { return [a.id, a.label]; }).concat([[CHECKLIST_AREA, 'Brand checklist']]), function (v) { state.area = v; renderList(); }),
    ]);
    var legend = el('p', { class: 'cpr-legend' }, ['Source: '].concat(['supplier', 'product', 'variant', 'inferred', 'setting', 'code'].map(function (k) {
      return el('span', { class: 'cpr-legend__item' }, [el('span', { class: 'cpr-src cpr-src--' + k, text: SOURCE[k].short, title: SOURCE[k].title }), ' ' + SOURCE[k].title.split(':')[0]]);
    })));
    chipsEl = el('div', { class: 'cpr-chips', role: 'group', 'aria-label': 'Gap filters' });
    countEl = el('p', { class: 'cpr-count' });
    listEl = el('div', { class: 'cpr-tablewrap' });
    /* Session 2: the Guide's "see these products" filter and a saved-change message. Session 4: the Catalog-Bot panel. */
    var guideChip = null;
    if (state.guideFilter) {
      var gname = '';
      Object.keys(d.suppliers).some(function (n) { if (d.suppliers[n].guideId === state.guideFilter) { gname = d.suppliers[n].guideName || n; return true; } return false; });
      guideChip = el('div', { class: 'cpr-chips' }, [el('button', { class: 'cpr-chip is-on', type: 'button', title: 'Show every supplier',
        onclick: function () { state.guideFilter = ''; try { history.replaceState(null, '', location.pathname); } catch (e) { /* ignore */ } renderShell(); } },
        ['Supplier from the Vendor Guide: ' + (gname || 'not matched to any product') + ' ', el('span', { class: 'cpr-chip__n', text: '×' })])]);
    }
    var flash = state.flash ? el('p', { class: 'cpr-flash', role: 'status', text: state.flash }) : null;
    state.flash = null;
    root.appendChild(el('div', { class: 'cpr-top' }, [viewSwitch() /* Session 9 */, controls, el('div', { class: 'cpr-row cpr-row--meta' }, [built, el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Rebuild now', onclick: rebuild })]), flash, catalogBotPanel(), jobsPanel(), accuracyPanel() /* CUSTOM_FREIGHTACCURACY_BY_CLAUDE */, legend, guideChip, chipsEl, countEl]));
    root.appendChild(listEl);
    bulkEl = el('div', { class: 'cpr-bulkbar', role: 'region', 'aria-label': 'Bulk edit' });
    root.appendChild(bulkEl);
    renderList();
  }

  function baseFilter() { return { q: state.q, supplier: state.supplier, status: state.status, vendorSet: state.guideFilter ? vendorsForGuide(state.data.suppliers, state.guideFilter) : null }; }

  function renderChips() {
    clear(chipsEl);
    var base = filterRows(state.data.products, state.keys, baseFilter());
    var counts = gapCounts(base);
    var anyRed = base.filter(function (p) { return p.gaps.some(function (g) { return state.redIds[g]; }); }).length;
    function chip(id, label, n, level) {
      var on = state.gap === id;
      return el('button', {
        class: 'cpr-chip cpr-chip--' + level + (on ? ' is-on' : ''), type: 'button', 'aria-pressed': on ? 'true' : 'false',
        onclick: function () { state.gap = on ? '' : id; state.shown = PAGE_SIZE; renderList(); },
      }, [label + ' ', el('span', { class: 'cpr-chip__n', text: String(n) })]);
    }
    chipsEl.appendChild(chip('any-red', 'Any red gap', anyRed, 'red'));
    state.data.registry.gaps.forEach(function (g) {
      if (counts[g.id]) chipsEl.appendChild(chip(g.id, g.label, counts[g.id], g.level));
    });
  }

  function srcBadge(c) {
    if (!c || !c.s || c.s === 'none') return null;
    var s = SOURCE[c.s] || SOURCE.none;
    return el('span', { class: 'cpr-src cpr-src--' + c.s, text: s.short, title: s.title + (c.why ? ' · ' + c.why : '') });
  }

  function renderList() {
    renderChips();
    clear(listEl);
    var f = baseFilter(); f.gap = state.gap; f.redIds = state.redIds;
    var rows = filterRows(state.data.products, state.keys, f);
    countEl.textContent = rows.length + ' product' + (rows.length === 1 ? '' : 's') + (state.gap ? ' with this gap' : '');
    if (!rows.length) { state.lastRows = []; renderBulkBar(); listEl.appendChild(notice('empty', 'No products match', 'Change the search or filters. This is not a loading problem.')); return; }
    /* Session 9: "Brand checklist" columns = one per brand checklist rule, each product's state read from the worker. */
    var checklist = state.area === CHECKLIST_AREA;
    var rules = state.rulesByArea[state.area] || [];
    if (checklist) {
      ensureChecks();
      if (!state.checks || state.checks.loading) { state.lastRows = []; renderBulkBar(); listEl.appendChild(notice('loading', 'Loading the brand checklist…')); return; }
      if (state.checks.error) { state.lastRows = []; renderBulkBar(); listEl.appendChild(notice('error', 'The brand checklist could not load', state.checks.error, el('button', { class: 'cpr-btn', type: 'button', text: 'Try again', onclick: function () { state.checks = null; renderList(); } }))); return; }
      rules = (state.checks.data.rules || []).map(function (r) { return { id: r.id, label: r.label, where: r.where, checklist: true }; });
    }
    var shownRows = rows.slice(0, state.shown);
    var allOn = shownRows.length && shownRows.every(function (p) { return state.ticked[p.h]; });
    var headTick = el('input', { type: 'checkbox', 'aria-label': 'Tick every product shown', class: 'cpr-tick' });
    headTick.checked = !!allOn;
    headTick.addEventListener('change', function () { shownRows.forEach(function (p) { if (headTick.checked) state.ticked[p.h] = true; else delete state.ticked[p.h]; }); renderList(); });
    var head = el('tr', null, [el('th', { scope: 'col', class: 'cpr-tickcol' }, [headTick]), el('th', { scope: 'col', text: 'Product' }), el('th', { scope: 'col', text: 'Supplier' }), el('th', { scope: 'col', text: 'Gaps' })]
      .concat(rules.map(function (r) { return el('th', { scope: 'col', text: r.label, title: r.where }); })));
    var body = el('tbody');
    state.lastRows = rows;
    shownRows.forEach(function (p) {
      var red = p.gaps.filter(function (g) { return state.redIds[g]; }).length;
      var info = p.gaps.length - red;
      var gapCell = el('td', { class: 'cpr-gaps' }, [
        red ? el('span', { class: 'cpr-pill cpr-pill--red', text: red + ' red' }) : null,
        info ? el('span', { class: 'cpr-pill cpr-pill--info', text: String(info) }) : null,
        !red && !info ? el('span', { class: 'cpr-muted', text: '-' }) : null,
      ]);
      var tick = el('input', { type: 'checkbox', class: 'cpr-tick', 'aria-label': 'Tick ' + p.t });
      tick.checked = !!state.ticked[p.h];
      tick.addEventListener('click', function (e) { e.stopPropagation(); });
      var tr = el('tr', { class: 'cpr-rowlink' + (state.open === p.h ? ' is-open' : '') + (state.ticked[p.h] ? ' is-ticked' : ''), tabindex: '0', 'aria-expanded': state.open === p.h ? 'true' : 'false' }, [
        el('td', { class: 'cpr-tickcol' }, [tick]),
        el('th', { scope: 'row', class: 'cpr-prod' }, [
          el('span', { class: 'cpr-prod__t', text: p.t }),
          el('span', { class: 'cpr-prod__h' }, [p.h + ' ', p.st !== 'active' ? el('span', { class: 'cpr-st', text: p.st }) : null, p.gaps.indexOf('needs-review') !== -1 ? el('span', { class: 'cpr-st cpr-st--new', text: 'new' }) : null]),
        ]),
        el('td', { text: p.v || '-' }),
        gapCell,
      ].concat(rules.map(function (r) {
        if (r.checklist) {
          var ck = checkCell(((state.checks.data.checks || {})[p.h] || {})[r.id]);
          return el('td', { class: 'cpr-cell cpr-check ' + ck.cls, title: ck.title, text: ck.text });
        }
        var c = cellFor(p, state.data.suppliers, r.id);
        return el('td', { class: 'cpr-cell' + (!c || c.s === 'none' ? ' cpr-cell--none' : '') }, [el('span', { text: c ? c.v : '-' }), ' ', srcBadge(c)]);
      })));
      tick.addEventListener('change', function () { if (tick.checked) state.ticked[p.h] = true; else delete state.ticked[p.h]; tr.classList.toggle('is-ticked', tick.checked); renderBulkBar(); });
      var toggle = function () { state.open = state.open === p.h ? null : p.h; try { history.replaceState(null, '', state.open ? '#p=' + encodeURIComponent(state.open) : location.pathname); } catch (e) { /* ignore */ } renderList(); };
      tr.addEventListener('click', toggle);
      tr.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
      body.appendChild(tr);
      if (state.open === p.h) body.appendChild(el('tr', { class: 'cpr-detailrow' }, [el('td', { colspan: String(4 + rules.length) }, [detail(p)])]));
    });
    listEl.appendChild(el('table', { class: 'cpr-table' }, [el('thead', null, [head]), body]));
    renderBulkBar();
    if (rows.length > state.shown) {
      listEl.appendChild(el('div', { class: 'cpr-more' }, [el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Show ' + Math.min(PAGE_SIZE, rows.length - state.shown) + ' more of ' + (rows.length - state.shown), onclick: function () { state.shown += PAGE_SIZE; renderList(); } })]));
    }
  }

  function detail(p) {
    var box = el('div', { class: 'cpr-detail' });
    var gaps = el('ul', { class: 'cpr-gaplist' }, p.gaps.length ? p.gaps.map(function (id) {
      var g = state.gapById[id] || { label: id, level: 'info' };
      return el('li', { class: 'cpr-gaplist__item cpr-gaplist__item--' + g.level, text: (g.level === 'red' ? 'Red: ' : '') + g.label });
    }) : [el('li', { class: 'cpr-muted', text: 'No gaps.' })]);
    box.appendChild(el('div', { class: 'cpr-detail__head' }, [
      el('strong', { text: p.t }),
      el('span', { class: 'cpr-muted', text: ' · ' + p.v + ' · ' + p.st + (p.live ? ' · on the site' : '') + ' · ' + p.vc + ' variant' + (p.vc === 1 ? '' : 's') }),
      p.gaps.indexOf('needs-review') !== -1 ? el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Mark reviewed', onclick: function (e) { e.stopPropagation(); markReviewed(p); } }) : null,
      /* Session 2: the door back to the record Product Rules edits. */
      guideOf(p) && guideOf(p).id ? el('a', { class: 'cpr-btn cpr-btn--ghost', href: '/pages/rep-vendor-guide#v=' + encodeURIComponent(guideOf(p).id), target: '_blank', rel: 'noopener', text: 'Open in Vendor Guide' }) : null,
    ]));
    box.appendChild(gaps);
    var ovb = overrideBox(p); // Session 4
    if (ovb) box.appendChild(ovb);
    state.data.registry.areas.forEach(function (a) {
      var rules = state.rulesByArea[a.id] || [];
      var rows = [];
      rules.forEach(function (r) {
        var c = cellFor(p, state.data.suppliers, r.id);
        rows.push(el('tr', null, [
          el('th', { scope: 'row', text: r.label }),
          el('td', { class: !c || c.s === 'none' ? 'cpr-cell--none' : null }, [el('span', { text: c ? c.v : '-' }), ' ', srcBadge(c), c && c.why ? el('span', { class: 'cpr-why', text: c.why }) : null]),
          el('td', { class: 'cpr-where', text: r.where }),
          el('td', { class: 'cpr-editcell' }, editButtons(p, r, c)),
        ]));
        var ed = state.editing;
        if (ed && ed.handle === p.h && ed.row === r.id) rows.push(el('tr', { class: 'cpr-editrow' }, [el('td', { colspan: '4' }, [editorBox(p, ed)])]));
      });
      box.appendChild(el('section', { class: 'cpr-area' }, [el('h3', { class: 'cpr-area__t', text: a.label }), el('table', { class: 'cpr-kv' }, [el('tbody', null, rows)])]));
    });
    box.appendChild(changeLog(p));
    var vbox = el('section', { class: 'cpr-area' }, [el('h3', { class: 'cpr-area__t', text: 'Variants' })]);
    box.appendChild(vbox);
    var cached = state.detail[p.h];
    if (cached) drawVariants(vbox, cached);
    else {
      vbox.appendChild(el('p', { class: 'cpr-muted', text: 'Loading variants…' }));
      api().get('/product-rules/product?handle=' + encodeURIComponent(p.h)).then(function (r) {
        state.detail[p.h] = r; vbox.removeChild(vbox.lastChild); drawVariants(vbox, r);
      }).catch(function (err) {
        vbox.removeChild(vbox.lastChild); vbox.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: 'Variants could not load: ' + ((err && err.message) || 'error') }));
      });
    }
    box.addEventListener('click', function (e) { e.stopPropagation(); });
    return box;
  }

  function drawVariants(box, r) {
    if (!r.variants) { box.appendChild(el('p', { class: 'cpr-muted', text: 'Too many variants to list (' + r.product.variants + '); the first ' + r.product.variantsRead + ' were read for weights and costs.' })); return; }
    var head = el('tr', null, ['SKU', 'Variant', 'Weight', 'Cost', 'Supplier SKU', 'PO unit', 'Min'].map(function (h) { return el('th', { scope: 'col', text: h }); }));
    var body = el('tbody', null, r.variants.map(function (v) {
      return el('tr', null, [v.sku || '-', v.title || '-', v.weightLb != null ? v.weightLb + ' lb' : 'none', v.cost != null ? '$' + Number(v.cost).toFixed(2) : '-', v.supplierSku || '-', v.poUom ? v.poUom + (v.poUomFactor ? ' x' + v.poUomFactor : '') : '-', v.moq || '-']
        .map(function (t) { return el('td', { text: String(t) }); }));
    }));
    box.appendChild(el('div', { class: 'cpr-tablewrap' }, [el('table', { class: 'cpr-table cpr-table--small' }, [el('thead', null, [head]), body])]));
  }

  function markReviewed(p) {
    api().post('/product-rules/reviewed', { handles: [p.h] }).then(function () {
      p.gaps = p.gaps.filter(function (g) { return g !== 'needs-review'; });
      renderList();
    }).catch(function (err) { alert('Could not mark it reviewed: ' + ((err && err.message) || 'error')); });
  }

  // ── Session 2: edits, the change log, the fold-in ───────────────────────────────────────────

  var WORDS = {
    'po-only': 'PO only', 'freight-then-po': 'Freight first, then PO', 'web-order': 'Web order',
    none: 'Nothing', ack: 'An acknowledgement', 'payment-link': 'A payment link',
    standard: 'Standard (we arrange freight)', 'parcel-only': 'Parcel only (they ship)', 'flat-rate': 'Flat rate (they ship)', 'freight-in-product': 'Freight in the price (they ship)',
    '': 'Not set (the agents ask Ryan)', 'we-arrange': 'We arrange the freight', 'we-arrange-quote': 'We arrange, customer pays (quote first)', 'locked-rate': 'Locked rate', 'vendor-ships': 'The supplier ships it',
    'per-order': 'Per order', 'per-product': 'Per product',
  };
  var EDIT_LABEL = { 'pallet-fee': 'Edit fee', 'pallet-fee-exclusion': 'This product', 'product-fee': 'Fee for this product', 'locked-rate': 'Edit for this product' };

  function supplierOf(p) { return state.data.suppliers[p.v] || null; }
  function guideOf(p) { var s = supplierOf(p); return s && s.guide ? s.guide : null; }

  /** The edits a rule row offers: its own, plus any that edit through it (the pallet-fee exclusion). */
  function editsFor(ruleId) {
    var edits = state.data.registry.edits || {};
    var out = [];
    var own = ruleId === 'locked-pallet' ? 'locked-rate' : ruleId;
    if (edits[own]) out.push(own);
    Object.keys(edits).forEach(function (k) { if (edits[k].of === ruleId) out.push(k); });
    return out;
  }

  function variantsOf(p) { var d = state.detail[p.h]; return d && Array.isArray(d.variants) ? d.variants : null; }

  function openEditor(p, rowId, rule, preset) {
    if (isProductDoor(rule)) {
      var spec = state.data.registry.edits[rule];
      var vs = variantsOf(p);
      var v0 = spec.level === 'variant' && vs && vs.length ? vs[0] : null;
      state.editing = { handle: p.h, row: rowId, rule: rule, door: 'product', variantId: v0 ? 'gid://shopify/ProductVariant/' + v0.id : '', value: preset || productEditValue(rule, p, v0), preview: null, error: null, busy: false };
      renderList();
      return;
    }
    var c = cellFor(p, state.data.suppliers, (rule === 'pallet-fee-exclusion' || rule === 'product-fee') ? 'pallet-fee' : (rule === 'locked-rate' ? 'locked-rate' : rule));
    state.editing = { handle: p.h, row: rowId, rule: rule, value: preset || editValue(rule, guideOf(p), c), preview: null, error: null, busy: false };
    renderList();
    if (preset) previewEdit(p);
  }

  function isProductDoor(rule) { var sp = (state.data.registry.edits || {})[rule]; return !!(sp && sp.door === 'product'); }

  function editButtons(p, r, c) {
    var all = editsFor(r.id);
    if (!all.length) return [];
    /* Session 3: product / variant rules write to Shopify, the seeds or the freight profile, never the Guide. */
    var pbtn = all.filter(isProductDoor).map(function (k) {
      var lab = (state.data.registry.edits[k].label || 'Edit').replace(' (one variant)', ' (variant)');
      return el('button', { class: 'cpr-linkbtn', type: 'button', text: lab, onclick: function (e) { e.stopPropagation(); openEditor(p, r.id, k); } });
    });
    var ids = all.filter(function (k) { return !isProductDoor(k); });
    /* Session 7: the Corona line is ECORE's own; another supplier's row offers no edit. */
    ids = ids.filter(function (k) { var sp = state.data.registry.edits[k] || {}; return !sp.vendor || new RegExp('\\b' + sp.vendor + '\\b', 'i').test(p.v + ' ' + ((guideOf(p) || {}).name || '')); });
    if (!ids.length) return pbtn;
    if (!guideOf(p)) return pbtn.concat([el('span', { class: 'cpr-muted', title: 'This supplier is not matched to one Vendor Guide record yet', text: 'not in the Guide' })]);
    var out = pbtn.concat(ids.map(function (k) {
      return el('button', { class: 'cpr-linkbtn', type: 'button', text: EDIT_LABEL[k] || 'Edit', onclick: function (e) { e.stopPropagation(); openEditor(p, r.id, k); } });
    }));
    if (r.id === 'freight-procedure' && c && c.s === 'inferred' && c.raw) {
      out.unshift(el('button', { class: 'cpr-linkbtn cpr-linkbtn--strong', type: 'button', text: 'Make explicit', title: 'Write "' + (WORDS[c.raw] || c.raw) + '" to the Vendor Guide so nobody has to infer it',
        onclick: function (e) { e.stopPropagation(); openEditor(p, r.id, 'freight-procedure', { mode: c.raw }); } }));
    }
    return out;
  }

  function field(label, input) { return el('label', { class: 'cpr-field' }, [el('span', { class: 'cpr-field__label', text: label }), input]); }
  function inp(name, value, type) { return el('input', { class: 'cpr-search', name: name, type: type || 'text', value: value == null ? '' : String(value), step: type === 'number' ? '0.01' : null, min: type === 'number' ? '0' : null }); }
  function sel(name, value, opts) {
    var s = el('select', { class: 'cpr-select', name: name }, opts.map(function (o) { var op = el('option', { value: o[0], text: o[1] }); if (String(o[0]) === String(value)) op.selected = true; return op; }));
    return s;
  }
  function chk(name, checked, label) {
    var c = el('input', { type: 'checkbox', name: name });
    c.checked = !!checked;
    return el('label', { class: 'cpr-check' }, [c, ' ' + label]);
  }

  function editorFields(rule, v) {
    var spec = state.data.registry.edits[rule] || {};
    switch (spec.kind) {
      case 'shipping-model': return [field('Shipping model', sel('model', v.model, spec.options.map(function (o) { return [o, WORDS[o] || o]; }))), field('Flat rate per order (flat rate only)', inp('flatRate', v.flatRate, 'number'))];
      case 'freight-procedure': return [
        field('Who does the freight work', sel('mode', v.mode, spec.options.map(function (o) { return [o, WORDS[o] || o]; }))),
        field('Locked rate basis', sel('basis', v.basis, [['', '-']].concat(spec.bases.map(function (o) { return [o, WORDS[o]]; })))),
        field('Rate per order (per-order basis)', inp('rate', v.rate, 'number')),
      ];
      case 'enum': return [field('Value', sel('value', v.value, spec.options.map(function (o) { return [o, WORDS[o] || o]; })))];
      case 'text': return [field('Value', inp('value', v.value))];
      case 'pallet-fee': return [field('Fee per piece ($, 0 = none)', inp('fee', v.fee, 'number')), field('Label on the PO', inp('label', v.label))];
      /* CUSTOM_SUPPLIERFEE_BY_CLAUDE (ONYX 41775fe3a0bb, Ryan 2026-10-16) - this product's own fee, for any supplier. */
      case 'product-fee': return [field('Fee per piece for this product ($, 0 = no fee; replaces the supplier\'s pallet fee)', inp('amount', v.amount, 'number')),
        field('Label on the PO (blank = the supplier\'s pallet fee label, or "Handling Fee")', inp('label', v.label)),
        chk('remove', v.remove, 'Remove this product\'s own fee (the supplier\'s pallet fee applies again)')];
      case 'locked-rate': {
        var pal = v.pallet || {};
        return [field('Rate ($)', inp('rate', v.rate, 'number')),
          field('Pallet length (in)', inp('length', pal.length, 'number')), field('Width (in)', inp('width', pal.width, 'number')),
          field('Height (in)', inp('height', pal.height, 'number')), field('Weight (lb)', inp('weightLb', pal.weightLb, 'number')),
          chk('remove', v.remove, 'Remove this product\'s locked rate')];
      }
      case 'tri': { var t = TRI[rule]; return [field('Value', sel('value', v.value, [['unset', 'Not set (counts as no)'], ['yes', t.words[0]], ['no', t.words[1]]]))]; }
      case 'stock': return [field('Stock', sel('stock', v.stock || '', [['', 'Follow the supplier (blank)'], ['in-stock', 'Usually in stock: no ready-date chases'], ['chase', 'Normal ship-date chases (ran out / takes longer)']]))]; // CUSTOM_USUALLYINSTOCK_BY_CLAUDE
      case 'pack': return [field('Packing type', sel('pack', v.pack || '', [['', 'Follow the supplier (blank)'], ['fixed-unit', 'Fixed unit: 10% cushion until one shipment is measured'], ['by-quantity', 'Sold by quantity: cushion by similar-size shipments'], ['stackable', 'Stackable boxes: box weight trusted, one empty pallet added']]))]; // CUSTOM_FREIGHTCUSHION_BY_CLAUDE (Freight Hub H11)
      case 'tag-bool': return [chk('on', v.on, rule === 'ships-free-ok' ? 'We sell this one with free shipping on purpose (tag ships-free-ok)' : 'Freight-quoted (tag freight-quoted)')];
      case 'unit': return [field('Unit of measure', sel('unit', v.unit, [['', 'None']].concat((spec.options || []).map(function (o) { return [o, 'unit:' + o]; }))))];
      case 'roll': return [field('Roll width (in)', inp('width', v.width, 'number')), field('Min roll length (ft)', inp('min', v.min, 'number')), field('Max roll length (ft)', inp('max', v.max, 'number'))];
      case 'po-unit': return [field('PO unit (e.g. sqft)', inp('uom', v.uom)), field('Factor (store units per PO unit)', inp('factor', v.factor, 'number'))];
      case 'int': return [field(rule === 'moq' ? 'Minimum order quantity' : 'Carton step (sold in multiples of)', inp('value', v.value, 'number'))];
      case 'bundle': return [field('Plunge SKU', inp('plunge', v.plunge)), field('Chiller SKU', inp('chiller', v.chiller))];
      case 'weight': return [field('Weight for every variant (lb)', inp('lb', v.lb, 'number'))];
      case 'lb': return [field('Weight (lb, blank = none)', inp('lb', v.lb, 'number'))];
      case 'seed': return [field('Seed weight (lb, blank = remove the seed)', inp('lb', v.lb, 'number'))];
      case 'profile-flags': return [
        field('Quotable', sel('quotable', 'keep', [['keep', 'Leave as it is'], ['true', 'Quotable'], ['false', 'Not quotable']])),
        field('Excluded from profiling', sel('excluded', 'keep', [['keep', 'Leave as it is'], ['false', 'No'], ['true', 'Yes, excluded']])),
        field('Why excluded', inp('excludedReason', '')),
        field('Rides on the pallet', sel('ridesOnPallet', 'keep', [['keep', 'Leave as it is'], ['false', 'No, ships as its own unit'], ['true', 'Yes, rides on the heaviest skid']])),
      ];
      case 'post-ship': return [chk('on', v.on, 'This supplier invoices after it ships (the Deal Dashboard waits for their freight invoice, not a quote)')];
      case 'states': return [field('Ship-to states (2-letter codes, comma separated; blank = none)', inp('value', v.value))];
      case 'bool': return [rule === 'pallet-fee-exclusion' ? chk('excluded', v.excluded, 'No pallet fee on this product') : chk('allowed', v.allowed, 'An AI agent may email this supplier a purchase order')];
      default: return [];
    }
  }

  function readForm(form, rule) {
    var g = function (n) { var x = form.querySelector('[name="' + n + '"]'); return x ? x : null; };
    var val = function (n) { var x = g(n); return x ? x.value : ''; };
    var on = function (n) { var x = g(n); return !!(x && x.checked); };
    var kind = (state.data.registry.edits[rule] || {}).kind;
    switch (kind) {
      case 'shipping-model': return { model: val('model'), flatRate: val('flatRate') };
      case 'freight-procedure': return { mode: val('mode'), basis: val('basis'), rate: val('rate') };
      case 'pallet-fee': return { fee: val('fee'), label: val('label') };
      case 'product-fee': return on('remove') ? { remove: true } : { amount: val('amount'), label: val('label') }; // CUSTOM_SUPPLIERFEE_BY_CLAUDE
      case 'locked-rate': return on('remove') ? { remove: true } : { rate: val('rate'), pallet: { length: val('length'), width: val('width'), height: val('height'), weightLb: val('weightLb') } };
      case 'bool': return rule === 'pallet-fee-exclusion' ? { excluded: on('excluded') } : { allowed: on('allowed') };
      case 'tag-bool': case 'post-ship': return { on: on('on') };
      case 'unit': return { unit: val('unit') };
      case 'stock': return { stock: val('stock') }; // CUSTOM_USUALLYINSTOCK_BY_CLAUDE
      case 'pack': return { pack: val('pack') }; // CUSTOM_FREIGHTCUSHION_BY_CLAUDE
      case 'roll': return { width: val('width'), min: val('min'), max: val('max') };
      case 'po-unit': return { uom: val('uom'), factor: val('factor') };
      case 'bundle': return { plunge: val('plunge'), chiller: val('chiller') };
      case 'weight': case 'lb': case 'seed': return { lb: val('lb') };
      case 'profile-flags': {
        var pf = {};
        ['quotable', 'excluded', 'ridesOnPallet'].forEach(function (k) { var x = val(k); if (x === 'true' || x === 'false') pf[k] = x === 'true'; });
        if (val('excludedReason')) pf.excludedReason = val('excludedReason');
        return pf;
      }
      default: return { value: val('value') };
    }
  }

  function writeBody(p, ed, apply) {
    var spec = state.data.registry.edits[ed.rule] || {};
    if (ed.door === 'product') {
      var pb = { rule: ed.rule, handle: p.h, value: ed.value, apply: !!apply };
      if (spec.level === 'variant') pb.variantId = ed.variantId;
      if (apply) pb.basedOn = ed.preview && ed.preview.basedOn;
      return pb;
    }
    var g = guideOf(p);
    var b = { rule: ed.rule, value: ed.value, basedOn: ed.rule === 'po-send' ? (state.data.poPolicy && state.data.poPolicy.rev) || ''
      : (spec.kind === 'post-ship' || spec.kind === 'states' ? (state.data.ruleSettings && state.data.ruleSettings.rev) || '' : (g ? g.rev : '')), apply: !!apply };
    if (spec.level === 'product') b.handle = p.h; else b.vendor = p.v;
    return b;
  }

  function previewEdit(p) {
    var ed = state.editing;
    ed.busy = true; ed.error = null; ed.preview = null; renderList();
    api().post('/product-rules/write', writeBody(p, ed, false)).then(function (res) {
      ed.busy = false; ed.preview = res; renderList();
    }).catch(function (err) {
      ed.busy = false; ed.error = err; renderList();
    });
  }

  function applyEdit(p) {
    var ed = state.editing;
    ed.busy = true; ed.error = null; renderList();
    api().post('/product-rules/write', writeBody(p, ed, true)).then(function (res) {
      state.editing = null; state.logs = {};
      if (ed.door === 'product') { state.flash = 'Saved: ' + (res.label || ed.rule) + ' on ' + p.t + ' (' + res.before + ' -> ' + res.after + '). Shopify and the index are updated.'; state.detail = {}; load(); return; }
      state.flash = 'Saved: ' + (res.label || ed.rule) + (res.words ? ' (' + res.words + ')' : '') + '. ' + ((res.affects && res.affects.level !== 'product') ? res.affects.products + ' product' + (res.affects.products === 1 ? '' : 's') + ' from ' + res.affects.supplier + ' now read it.' : '');
      load();
    }).catch(function (err) {
      ed.busy = false; ed.error = err; renderList();
    });
  }

  function editorBox(p, ed) {
    var spec = state.data.registry.edits[ed.rule] || {};
    var form = el('form', { class: 'cpr-editor', onsubmit: function (e) { e.preventDefault(); ed.value = readForm(form, ed.rule); previewEdit(p); } });
    form.addEventListener('click', function (e) { e.stopPropagation(); });
    var where = spec.level === 'setting' ? 'the PO send policy (AI Agents)' : 'the Vendor Guide record for ' + ((supplierOf(p) && supplierOf(p).guideName) || p.v);
    if (ed.door === 'product') {
      form.appendChild(el('p', { class: 'cpr-editor__t', text: (spec.level === 'variant' ? 'One variant of this product, in ' : 'This product, in ') + (STORE_WORDS[spec.store] || spec.store) }));
      if (spec.level === 'variant') {
        var vs = variantsOf(p);
        if (!vs) {
          form.appendChild(el('p', { class: 'cpr-muted', text: state.detail[p.h] ? 'This product has too many variants to list here; edit the variant in Shopify.' : 'The variants are still loading; close this and open it again in a moment.' }));
        } else {
          var vsel = sel('variantId', ed.variantId, vs.map(function (x) { return ['gid://shopify/ProductVariant/' + x.id, (x.sku || '-') + ' · ' + (x.title || '')]; }));
          vsel.addEventListener('change', function () {
            var pick = vs.filter(function (x) { return 'gid://shopify/ProductVariant/' + x.id === vsel.value; })[0];
            ed.variantId = vsel.value; ed.value = productEditValue(ed.rule, p, pick); ed.preview = null; renderList();
          });
          form.appendChild(el('div', { class: 'cpr-editor__fields' }, [field('Variant', vsel)]));
        }
      }
    } else {
      form.appendChild(el('p', { class: 'cpr-editor__t', text: (spec.level === 'product' ? 'This product, in ' : 'Every product from this supplier, in ') + where }));
    }
    form.appendChild(el('div', { class: 'cpr-editor__fields' }, editorFields(ed.rule, ed.value || {})));
    var pv = ed.preview;
    if (pv) {
      var diff = (pv.diff || []).map(function (d) { return el('li', null, [el('code', { text: d.field }), ' ', el('span', { class: 'cpr-muted', text: shortVal(d.before) }), ' → ', el('strong', { text: shortVal(d.after) })]); });
      var changed = (pv.diff && pv.diff.length) || (pv.before && pv.after && pv.before !== pv.after);
      form.appendChild(el('div', { class: 'cpr-preview' }, [
        el('p', { text: pv.summary || '' }),
        diff.length ? el('ul', { class: 'cpr-diff' }, diff) : null,
        pv.confirm ? el('p', { class: 'cpr-confirm', text: pv.confirm }) : null,
        pv.undeclaredClears && pv.undeclaredClears.length ? el('p', { class: 'cpr-meta cpr-meta--bad', text: 'This would also empty ' + pv.undeclaredClears.join(', ') + ', which this edit did not ask for, so it cannot be saved here. Use the Vendor Guide.' }) : null,
      ]));
      form.appendChild(el('div', { class: 'cpr-row cpr-row--start' }, [
        changed && !(pv.undeclaredClears && pv.undeclaredClears.length)
          ? el('button', { class: 'cpr-btn', type: 'button', disabled: ed.busy ? 'disabled' : null, text: ed.busy ? 'Saving…' : (ed.door === 'product' ? 'Save' : (spec.level === 'setting' ? 'Save the setting' : 'Save to the Vendor Guide')), onclick: function () { applyEdit(p); } })
          : null,
        el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Change it', onclick: function () { ed.preview = null; renderList(); } }),
        el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: changed ? 'Cancel' : 'Close', onclick: function () { state.editing = null; renderList(); } }),
      ]));
    } else {
      form.appendChild(el('div', { class: 'cpr-row cpr-row--start' }, [
        el('button', { class: 'cpr-btn', type: 'submit', disabled: ed.busy ? 'disabled' : null, text: ed.busy ? 'Checking…' : 'Preview the change' }),
        el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Cancel', onclick: function () { state.editing = null; renderList(); } }),
      ]));
    }
    if (ed.error) {
      var stale = ed.error.status === 409 && ed.error.data && ed.error.data.code === 'stale';
      form.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', role: 'alert' }, [(ed.error.message || 'The change could not be saved') + ' ', stale ? el('button', { class: 'cpr-linkbtn', type: 'button', text: 'Reload', onclick: function () { state.editing = null; load(); } }) : null]));
    }
    return form;
  }

  var STORE_WORDS = { 'shopify-tag': 'the Shopify product tags', 'shopify-metafield': 'the Shopify metafields', 'shopify-native': 'the Shopify variant weight', 'unit-weight-seeds': 'the weight seeds', 'freight-profile': 'the freight profile' };

  function shortVal(v) {
    var s = v == null || v === '' ? '(empty)' : String(v);
    return s.length > 140 ? s.slice(0, 140) + '…' : s;
  }

  var KIND_WORDS = { 'product-rules': 'Product Rules', 'guide-ui': 'Vendor Guide screen', agent: 'AI agent', 'guide-api': 'API' };

  function changeLog(p) {
    var g = guideOf(p);
    var sec = el('section', { class: 'cpr-area' }, [el('h3', { class: 'cpr-area__t', text: 'Recent changes (this supplier and product)' })]);
    if (!g) { sec.appendChild(el('p', { class: 'cpr-muted', text: 'No Vendor Guide record, so no Guide history.' })); return sec; }
    var cached = state.logs[p.h];
    if (!cached) {
      sec.appendChild(el('p', { class: 'cpr-muted', text: 'Loading the change log…' }));
      state.logs[p.h] = { loading: true };
      api().get('/product-rules/log?guideId=' + encodeURIComponent(g.id) + '&vendor=' + encodeURIComponent(p.v) + '&handle=' + encodeURIComponent(p.h) + '&limit=15').then(function (r) {
        state.logs[p.h] = { entries: r.entries || [], guideUi: r.guideUi };
        if (state.open === p.h) renderList();
      }).catch(function (err) {
        state.logs[p.h] = { error: (err && err.message) || 'error' };
        if (state.open === p.h) renderList();
      });
      return sec;
    }
    if (cached.loading) { sec.appendChild(el('p', { class: 'cpr-muted', text: 'Loading the change log…' })); return sec; }
    if (cached.error) { sec.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: 'The change log could not load: ' + cached.error })); return sec; }
    if (cached.guideUi && cached.guideUi !== 'ok' && cached.guideUi !== 'not-asked') sec.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: 'Edits made on the Vendor Guide screen could not be loaded (' + cached.guideUi + ').' }));
    if (!cached.entries.length) { sec.appendChild(el('p', { class: 'cpr-muted', text: 'No recorded changes yet.' })); return sec; }
    sec.appendChild(el('ul', { class: 'cpr-log' }, cached.entries.map(function (e) {
      var d = new Date(e.at);
      var when = isNaN(d.getTime()) ? e.at : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      var fields = (e.fields || []).slice(0, 4).map(function (f) { return el('div', { class: 'cpr-log__f' }, [el('code', { text: f.field }), ' ', el('span', { class: 'cpr-muted', text: shortVal(f.before) }), ' → ', shortVal(f.after)]); });
      return el('li', { class: 'cpr-log__e' }, [
        el('div', null, [el('strong', { text: when }), ' · ' + (e.by || '?') + ' · ', el('span', { class: 'cpr-st', text: KIND_WORDS[e.kind] || e.kind }), e.forThisProduct ? el('span', { class: 'cpr-st cpr-st--new', text: 'this product' }) : null]),
        e.note ? el('div', { class: 'cpr-muted', text: e.note }) : null,
        e.askedBy ? el('div', { class: 'cpr-muted', text: 'Asked by: ' + e.askedBy }) : null, // CUSTOM_VENDORHISTORY_BY_CLAUDE
      ].concat(fields).concat((e.fieldCount || 0) > fields.length ? [el('div', { class: 'cpr-muted', text: '+' + (e.fieldCount - fields.length) + ' more field(s)' })] : []));
    })));
    return sec;
  }

  // ── Session 4 (Ryan 2026-10-09): Catalog-Bot access + its proposals ──────────────────────────
  /* The three old supplier lists (and the "Move them into the Vendor Guide" panel that emptied them) are
     retired: each supplier's answer is on its Vendor Guide record. This panel holds the switch that lets
     Catalog-Bot's key call Product Rules (absent = off) and the guesses it filed for Ryan to accept. */
  function loadCatalogBot() {
    state.cb = { busy: true };
    Promise.all([api().get('/admin/product-rules-catalogbot-config'), api().get('/product-rules/proposals?status=open')]).then(function (r) {
      state.cb = { config: r[0].config, keySet: r[0].keySet, proposals: (r[1] && r[1].proposals) || [] };
      renderShell();
    }).catch(function (err) { state.cb = { error: 'Catalog-Bot settings could not load: ' + ((err && err.message) || 'error') }; renderShell(); });
  }

  function catalogBotPanel() {
    if (!state.cb) { loadCatalogBot(); return el('p', { class: 'cpr-muted', text: 'Loading Catalog-Bot access…' }); }
    var cb = state.cb;
    if (cb.busy && !cb.config) return el('p', { class: 'cpr-muted', text: 'Loading Catalog-Bot access…' });
    if (cb.error && !cb.config) return el('p', { class: 'cpr-meta cpr-meta--bad', text: cb.error });
    var on = !!(cb.config && cb.config.enabled);
    var box = el('div', { class: 'cpr-panel', id: 'cpr-catalogbot' });
    box.appendChild(el('div', { class: 'cpr-row cpr-row--start' }, [
      el('p', { class: 'cpr-panel__t', text: 'Catalog-Bot access: ' + (on ? 'On' : 'Off') }),
      el('button', { class: 'cpr-btn' + (on ? ' cpr-btn--ghost' : ''), type: 'button', disabled: cb.busy ? 'disabled' : null, text: on ? 'Turn off' : 'Turn on', onclick: function () { setCatalogBot(!on); } }),
    ]));
    box.appendChild(el('p', { class: 'cpr-muted', text: on
      ? 'Catalog-Bot may read the gaps for a new batch, write facts from a supplier document (an empty variant weight, a missing locked rate and pallet), create a name-only Vendor Guide record for a new supplier, and file guesses below for you. It never accepts its own guesses.'
      : 'Off: Catalog-Bot\'s key is refused on every Product Rules call. Its go-live gate then cannot run, so nothing it builds goes live.' }));
    if (!cb.keySet) box.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: 'The worker has no Catalog-Bot key yet (the CATALOG_BOT_TOKEN secret), so the switch has nothing to let through.' }));
    if (cb.error) box.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: cb.error }));
    var props = cb.proposals || [];
    box.appendChild(el('p', { class: 'cpr-panel__t', text: 'Catalog-Bot proposals (' + props.length + ')' }));
    if (!props.length) box.appendChild(el('p', { class: 'cpr-muted', text: 'Nothing waiting.' }));
    else box.appendChild(el('table', { class: 'cpr-kv' }, [el('tbody', null, props.map(function (pr) {
      return el('tr', null, [
        el('th', { scope: 'row' }, [el('a', { href: '#p=' + encodeURIComponent(pr.handle), text: pr.title || pr.handle })]),
        el('td', { text: (pr.words || pr.kind) + (pr.vendor ? ' · ' + pr.vendor : '') }),
        el('td', { class: 'cpr-editcell' }, [
          el('button', { class: 'cpr-btn', type: 'button', disabled: cb.busy ? 'disabled' : null, text: 'Accept', onclick: function () { decideProposal(pr, 'accept'); } }),
          el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', disabled: cb.busy ? 'disabled' : null, text: 'Reject', onclick: function () { decideProposal(pr, 'reject'); } }),
        ]),
      ]);
    }))]));
    return box;
  }

  function setCatalogBot(enabled) {
    state.cb.busy = true; renderShell();
    api().post('/admin/product-rules-catalogbot-config', { enabled: enabled }).then(function (r) {
      state.cb.config = r.config; state.cb.keySet = r.keySet; state.cb.busy = false; state.cb.error = null; renderShell();
    }).catch(function (err) { state.cb.busy = false; state.cb.error = 'Could not save: ' + ((err && err.data && err.data.error) || (err && err.message) || 'error'); renderShell(); });
  }

  function decideProposal(pr, action) {
    var body = { handle: pr.handle, kind: pr.kind };
    if (action === 'reject') { var why = window.prompt('Why reject it? (optional; Catalog-Bot will not file the same guess again)', ''); if (why === null) return; body.reason = why; }
    state.cb.busy = true; renderShell();
    api().post('/product-rules/proposal/' + action, body).then(function () {
      state.cb = null; state.flash = (action === 'accept' ? 'Accepted: ' : 'Rejected: ') + (pr.words || pr.kind) + ' on ' + (pr.title || pr.handle) + '.';
      if (action === 'accept') load(); else renderShell();
    }).catch(function (err) { state.cb.busy = false; state.cb.error = 'Could not ' + action + ': ' + ((err && err.data && err.data.error) || (err && err.message) || 'error'); renderShell(); });
  }

  /* The go-live override, in the row detail: an admin's reason, logged; it covers the red gaps it names. */
  function overrideBox(p) {
    var red = (p.gaps || []).filter(function (id) { return state.redIds[id]; });
    if (!red.length && !p.ov) return null;
    var sec = el('section', { class: 'cpr-area cpr-override' }, [el('h3', { class: 'cpr-area__t', text: 'Go-live gate' })]);
    if (p.ov) {
      var newRed = red.filter(function (id) { return (p.ov.red || []).indexOf(id) === -1; });
      sec.appendChild(el('p', { text: 'Allowed to go live anyway by ' + (p.ov.by || '?') + ' ' + ageText(Date.parse(p.ov.at)) + ': ' + p.ov.reason }));
      if (newRed.length) sec.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: 'A new red gap since then blocks it again: ' + newRed.map(function (id) { return (state.gapById[id] || { label: id }).label; }).join('; ') + '.' }));
      sec.appendChild(el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Remove the override', onclick: function () { saveOverride(p, { clear: true }); } }));
      return sec;
    }
    sec.appendChild(el('p', { class: 'cpr-muted', text: 'Catalog-Bot will not make this product live while it has a red gap.' }));
    if (state.ovEdit !== p.h) {
      sec.appendChild(el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Allow go-live anyway', onclick: function () { state.ovEdit = p.h; renderList(); } }));
      return sec;
    }
    var input = el('input', { class: 'cpr-search cpr-input', type: 'text', placeholder: 'Reason (logged), e.g. dims coming from the supplier Monday', 'aria-label': 'Reason' });
    sec.appendChild(el('div', { class: 'cpr-row cpr-row--start' }, [input,
      el('button', { class: 'cpr-btn', type: 'button', text: 'Save override', onclick: function () { saveOverride(p, { reason: input.value }); } }),
      el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Cancel', onclick: function () { state.ovEdit = null; renderList(); } })]));
    return sec;
  }

  function saveOverride(p, extra) {
    api().post('/product-rules/override', Object.assign({ handle: p.h }, extra)).then(function (r) {
      state.ovEdit = null;
      if (extra.clear) delete p.ov; else p.ov = r.override;
      renderList();
    }).catch(function (err) { alert('Could not save the override: ' + ((err && err.data && err.data.error) || (err && err.message) || 'error')); });
  }

  // ── Session 3: bulk edit (ticks + a bottom bar; Ryan's pick 2026-10-07) and the jobs list ─────

  function bulkSpecs() {
    var e = state.data.registry.edits || {};
    return Object.keys(e).filter(function (k) { return e[k].door === 'product' && e[k].bulk; });
  }

  function renderBulkBar() {
    if (!bulkEl) return;
    clear(bulkEl);
    var handles = tickedHandles(state.data.products, state.ticked);
    var b = state.bulk;
    if (!handles.length && !b) { bulkEl.className = 'cpr-bulkbar'; return; }
    bulkEl.className = 'cpr-bulkbar is-on';
    var shown = (state.lastRows || []).slice(0, state.shown);
    bulkEl.appendChild(el('div', { class: 'cpr-row cpr-row--start' }, [
      el('strong', { text: handles.length + ' ticked' }),
      shown.length ? el('button', { class: 'cpr-linkbtn', type: 'button', text: 'Select all ' + shown.length + ' shown', onclick: function () { shown.forEach(function (p) { state.ticked[p.h] = true; }); renderList(); } }) : null,
      el('button', { class: 'cpr-linkbtn', type: 'button', text: 'Clear', onclick: function () { state.ticked = {}; state.bulk = null; renderList(); } }),
    ]));
    if (!b || !b.job) {
      var specs = bulkSpecs();
      var rule = (b && b.rule) || specs[0];
      var edits = state.data.registry.edits;
      var rsel = sel('rule', rule, specs.map(function (k) { return [k, edits[k].label]; }));
      rsel.addEventListener('change', function () { state.bulk = { rule: rsel.value }; renderBulkBar(); });
      var form = el('form', { class: 'cpr-editor cpr-editor--bulk', onsubmit: function (e) { e.preventDefault(); bulkPreview(handles, rule, readForm(form, rule)); } });
      form.appendChild(el('div', { class: 'cpr-editor__fields' }, [field('Change', rsel)].concat(editorFields(rule, productEditValue(rule, null, null)))));
      form.appendChild(el('div', { class: 'cpr-row cpr-row--start' }, [el('button', { class: 'cpr-btn', type: 'submit', disabled: !handles.length || (b && b.busy) ? 'disabled' : null, text: b && b.busy ? 'Checking…' : 'Preview' })]));
      if (b && b.error) form.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', role: 'alert', text: b.error }));
      bulkEl.appendChild(form);
      return;
    }
    var job = b.job;
    var changed = job.rows.filter(function (r) { return r.change; });
    var other = job.rows.filter(function (r) { return !r.change; });
    bulkEl.appendChild(el('p', { class: 'cpr-editor__t', text: job.label + ': ' + (b.confirm || 'Nothing to change: every ticked product already says this.') }));
    bulkEl.appendChild(el('div', { class: 'cpr-bulkrows' }, [el('table', { class: 'cpr-table cpr-table--small' }, [
      el('thead', null, [el('tr', null, ['Product', 'Before', 'After', 'State'].map(function (h) { return el('th', { scope: 'col', text: h }); }))]),
      el('tbody', null, changed.concat(other).map(function (r) {
        return el('tr', { class: r.change ? null : 'cpr-cell--none' }, [el('td', { text: r.t }), el('td', { text: r.before || '-' }), el('td', { text: r.change ? r.after : '-' }), el('td', { text: (r.state || '') + (r.why || r.error ? ': ' + (r.why || r.error) : '') })]);
      })),
    ])]));
    if (b.progress) bulkEl.appendChild(el('p', { class: 'cpr-meta', role: 'status', text: b.progress }));
    if (b.error) bulkEl.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', role: 'alert', text: b.error }));
    var running = job.status === 'staged' || job.status === 'applying';
    bulkEl.appendChild(el('div', { class: 'cpr-row cpr-row--start' }, [
      running ? el('button', { class: 'cpr-btn', type: 'button', disabled: b.busy ? 'disabled' : null, text: b.busy ? 'Applying…' : (job.status === 'applying' ? 'Continue applying' : 'Apply to ' + changed.length + ' product' + (changed.length === 1 ? '' : 's')), onclick: function () { bulkRun(job.id, 'apply'); } }) : null,
      job.status === 'staged' && !b.busy ? el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Cancel', onclick: function () { api().post('/product-rules/bulk', { action: 'cancel', jobId: job.id }).catch(function () { /* the job just expires */ }); state.bulk = null; renderBulkBar(); } }) : null,
      job.status === 'nothing' ? el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Back', onclick: function () { state.bulk = { rule: job.rule }; renderBulkBar(); } }) : null,
      !running && job.status !== 'nothing' ? el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'Done', onclick: function () { state.bulk = null; state.ticked = {}; state.detail = {}; load(); } }) : null,
    ]));
  }

  function bulkPreview(handles, rule, value) {
    state.bulk = { rule: rule, busy: true }; renderBulkBar();
    api().post('/product-rules/bulk', { action: 'preview', rule: rule, value: value, handles: handles }).then(function (r) {
      state.bulk = { rule: rule, job: r.job, confirm: r.confirm }; renderBulkBar();
    }).catch(function (err) { state.bulk = { rule: rule, error: (err && err.message) || 'The preview failed' }; renderBulkBar(); });
  }

  /* The worker writes one chunk per call; this keeps calling until the job says done. An undo started
     from the jobs list runs with no bar open and reports through the flash line. */
  function bulkRun(jobId, action) {
    var b = action === 'apply' ? (state.bulk || {}) : { undo: true };
    if (action === 'apply') { b.busy = true; b.error = null; renderBulkBar(); }
    var step = function () {
      api().post('/product-rules/bulk', { action: action, jobId: jobId }).then(function (r) {
        var c = r.job.counts;
        b.job = action === 'apply' ? r.job : null;
        b.progress = (action === 'apply' ? 'Applied ' + c.applied + ' of ' + c.changed : 'Undone ' + c.undone + ' of ' + c.applied) + (c.skipped ? ' · skipped ' + c.skipped : '') + (c.failed ? ' · failed ' + c.failed : '') + (r.done ? '' : ' · working…');
        if (action === 'apply') renderBulkBar();
        if (!r.done) { setTimeout(step, 1200); return; }
        b.busy = false; state.jobs = null; state.logs = {};
        if (action === 'apply') renderBulkBar(); else { state.flash = 'Undone: ' + r.job.label + '. ' + b.progress; state.detail = {}; load(); }
      }).catch(function (err) {
        b.busy = false; b.error = ((err && err.message) || 'The step failed') + '. Nothing after the last finished product was written; you can continue.';
        if (action === 'apply') renderBulkBar(); else { state.flash = b.error; renderShell(); }
      });
    };
    step();
  }

  var JOB_WORDS = { staged: 'previewed, not applied', applying: 'applying (unfinished)', applied: 'applied', 'applied-partly': 'applied (some skipped)', undoing: 'undoing (unfinished)', undone: 'undone', cancelled: 'cancelled', failed: 'failed' };

  function jobsPanel() {
    var box = el('details', { class: 'cpr-jobs' });
    if (state.jobsOpen) box.setAttribute('open', 'open');
    box.appendChild(el('summary', { text: 'Bulk edits (with Undo)' }));
    box.addEventListener('toggle', function () {
      state.jobsOpen = box.open;
      if (box.open && !state.jobs) {
        state.jobs = { loading: true };
        /* Redraw only this panel: a whole-page redraw would eat a just-shown "Saved" / "Undone" line. */
        var swap = function () { if (box.parentNode) box.parentNode.replaceChild(jobsPanel(), box); };
        api().get('/product-rules/jobs?limit=20').then(function (r) { state.jobs = { list: r.jobs || [] }; swap(); })
          .catch(function (err) { state.jobs = { error: (err && err.message) || 'error' }; swap(); });
      }
    });
    var j = state.jobs;
    if (!j || j.loading) { box.appendChild(el('p', { class: 'cpr-muted', text: 'Loading…' })); return box; }
    if (j.error) { box.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: 'The jobs could not load: ' + j.error })); return box; }
    if (!j.list.length) { box.appendChild(el('p', { class: 'cpr-muted', text: 'No bulk edits yet.' })); return box; }
    box.appendChild(el('table', { class: 'cpr-kv' }, [el('tbody', null, j.list.map(function (r) {
      var d = new Date(r.at);
      var undoable = r.status === 'applied' || r.status === 'applied-partly' || r.status === 'undoing';
      return el('tr', null, [
        el('th', { scope: 'row', text: isNaN(d.getTime()) ? r.at : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) }),
        el('td', { text: r.label + ' · ' + r.products + ' product' + (r.products === 1 ? '' : 's') + ' · ' + (r.by || '?') }),
        el('td', { text: JOB_WORDS[r.status] || r.status }),
        el('td', null, [undoable ? el('button', { class: 'cpr-linkbtn', type: 'button', text: 'Undo', onclick: function () {
          if (!window.confirm('Undo "' + r.label + '" on ' + r.applied + ' product(s)? Any product changed since is left alone.')) return;
          bulkRun(r.id, 'undo');
        } }) : null]),
      ]);
    }))]));
    return box;
  }

  /* CUSTOM_FREIGHTACCURACY_BY_CLAUDE (ONYX 0c2fb200e329, Ryan 2026-10-12) - how close our freight calculator and the supplier's
     first weight & dims reply came to what actually shipped, per product (GET /freight-accuracy). Report only. */

  /** "4% · low" for one guess's numbers, or a dash with no logs. Pure. */
  function accCell(st) {
    if (!st || !st.n) return { text: '-', cls: 'cpr-acc--none' };
    var bias = st.bias === 'high' ? 'runs high' : st.bias === 'low' ? 'runs low' : 'even';
    var hint = st.hint === 'trust' ? 'cpr-acc--trust' : st.hint === 'weak' ? 'cpr-acc--weak' : 'cpr-acc--wait';
    return { text: st.medianAbsLbPct + '% · ' + bias + ' (' + st.n + ')', cls: hint };
  }

  function accuracyPanel() {
    var box = el('details', { class: 'cpr-jobs cpr-acc' });
    if (state.accOpen) box.setAttribute('open', 'open');
    box.appendChild(el('summary', { text: 'Freight accuracy: calculator vs supplier, against what shipped' }));
    box.addEventListener('toggle', function () {
      state.accOpen = box.open;
      if (box.open && !state.acc) {
        state.acc = { loading: true };
        var swap = function () { if (box.parentNode) box.parentNode.replaceChild(accuracyPanel(), box); };
        api().get('/freight-accuracy').then(function (r) { state.acc = { data: r }; swap(); })
          .catch(function (err) { state.acc = { error: (err && err.message) || 'error' }; swap(); });
      }
    });
    var a = state.acc;
    if (!a || a.loading) { box.appendChild(el('p', { class: 'cpr-muted', text: 'Loading…' })); return box; }
    if (a.error) { box.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: 'The accuracy report could not load: ' + a.error })); return box; }
    var d = a.data || {};
    var products = d.products || [];
    var bf = d.backfill;
    box.appendChild(el('p', { class: 'cpr-muted', text: 'Each measured shipment (ready notice, BOL or carrier reweigh) is compared with our calculator’s estimate and the supplier’s first weight & dims reply. Typical weight miss, whether the guess runs high or low, and how many shipments, per product. A load with several products counts for each of them; "single-product loads" counts only the clean ones. Green = within ' + 10 + '% over 3+ shipments. This changes nothing on its own.'
      + (bf && !bf.finished ? ' Still reading past shipments (' + bf.done + ' of ' + bf.seeded + ').' : '') }));
    if (!products.length) { box.appendChild(el('p', { class: 'cpr-muted', text: 'No measured shipment has a guess on file to compare yet.' })); return box; }
    var head = el('tr', null, ['Product', 'Supplier', 'Shipments', 'Calculator', 'Supplier first reply', 'Calculator, single-product loads', 'Supplier, single-product loads'].map(function (h) { return el('th', { scope: 'col', text: h }); }));
    var body = el('tbody', null, products.map(function (p) {
      var cells = [p.calc, p.supplier, p.single && p.single.calc, p.single && p.single.supplier].map(function (st) { var c = accCell(st); return el('td', { class: c.cls, text: c.text }); });
      return el('tr', null, [el('td', { text: p.title || p.key || (p.skus || []).join(', ') }), el('td', { text: p.vendor || '' }), el('td', { text: p.n + (p.nSingle !== p.n ? ' (' + p.nSingle + ' single)' : '') })].concat(cells));
    }));
    box.appendChild(el('div', { class: 'cpr-tablewrap' }, [el('table', { class: 'cpr-table cpr-acc__table' }, [el('thead', null, [head]), body])]));
    return box;
  }

  // ── Session 8 (Ryan 2026-10-11): the brand checklist; Session 9: the Brands view ─────────────
  /* Counts per rule per brand ("5/10", a tick at 10/10), each supplier's Vendor Guide checklist, and (Session 9)
     Catalog-Bot's build-out steps grouped by stage. Done is always worked out (the rules and the Guide by the worker
     from what is on file, the steps by Catalog-Bot's tracker); the only thing this view sets is "n/a", with a reason.
     Ryan's pick (2026-10-11): a view inside Product Rules, not its own page; the product view is the Products table
     with the "Brand checklist" columns, filtered to one brand. */

  /** "5/10", a tick when complete, "n/a" when marked. Pure. */
  function fraction(done, needed, complete, na) {
    if (na) return 'n/a';
    if (complete) return '✓';
    return String(done) + '/' + String(needed);
  }

  /** One stage's cell: "✓", "⌛" (only deferred steps), else "done/needed". Pure. */
  function stageCell(st) {
    if (!st) return { text: '-', cls: 'cpr-stage--none' };
    if (st.deferred) return { text: '⌛', cls: 'cpr-stage--deferred' };
    if (st.complete) return { text: '✓', cls: 'cpr-stage--done' };
    var cls = st.blocked ? 'cpr-stage--blocked' : (st.inProgress || st.done ? 'cpr-stage--part' : 'cpr-stage--todo');
    return { text: st.done + '/' + st.needed, cls: cls };
  }

  /** One product's state for one rule, for the Products view's checklist columns. Pure. */
  function checkCell(state) {
    switch (state) {
      case 'done': return { text: '✓', cls: 'cpr-check--done', title: 'On file' };
      case 'missing': return { text: '✗', cls: 'cpr-check--missing', title: 'Missing' };
      case 'na': return { text: 'n/a', cls: 'cpr-check--na', title: 'Marked n/a for this brand' };
      case 'unknown': return { text: '?', cls: 'cpr-check--na', title: 'Not counted until the next rebuild' };
      default: return { text: '-', cls: 'cpr-check--na', title: 'Does not apply to this product' };
    }
  }

  /* Session 9: `#view=brands` opens the Brands view. */
  function parseViewHash(hash) {
    return /[#&]view=brands\b/.test(String(hash || '')) ? 'brands' : 'products';
  }

  function setView(v) {
    state.view = v;
    try { history.replaceState(null, '', v === 'brands' ? '#view=brands' : location.pathname); } catch (e) { /* ignore */ }
    renderShell();
  }

  function viewSwitch() {
    function tab(id, label) {
      var on = state.view === id;
      return el('button', { class: 'cpr-tab' + (on ? ' is-on' : ''), type: 'button', role: 'tab', 'aria-selected': on ? 'true' : 'false',
        onclick: function () { if (!on) setView(id); } }, [label]);
    }
    return el('div', { class: 'cpr-tabs', role: 'tablist', 'aria-label': 'View' }, [tab('products', 'Products'), tab('brands', 'Brands')]);
  }

  function errText(err) { return (err && err.data && err.data.error) || (err && err.message) || 'error'; }

  /** The Products view's checklist columns need every product's state per rule: one call, kept until reload. */
  function ensureChecks() {
    if (state.checks) return;
    state.checks = { loading: true };
    api().get('/product-rules/brands?checks=1').then(function (r) { state.checks = { data: r }; if (state.view === 'products') renderList(); })
      .catch(function (err) { state.checks = { error: errText(err) }; if (state.view === 'products') renderList(); });
  }

  function reloadBrands() { state.brands = null; state.brandDetail = null; state.checks = null; renderShell(); }

  function renderBrandsView() {
    clear(root);
    var top = el('div', { class: 'cpr-top' }, [viewSwitch()]);
    root.appendChild(top);
    var b = state.brands;
    if (!b) {
      state.brands = { loading: true };
      api().get('/product-rules/brands').then(function (r) { state.brands = { data: r }; if (state.view === 'brands') renderBrandsView(); })
        .catch(function (err) { state.brands = { error: errText(err) }; if (state.view === 'brands') renderBrandsView(); });
      b = state.brands;
    }
    if (b.loading) { root.appendChild(notice('loading', 'Loading every brand…')); return; }
    if (b.error) { root.appendChild(notice('error', 'The brands could not load', b.error + '. This is a loading problem, not an empty list.', el('button', { class: 'cpr-btn', type: 'button', text: 'Try again', onclick: reloadBrands }))); return; }
    var d = b.data;
    var prog = d.progress;
    var stages = prog ? prog.stages : [];
    top.appendChild(el('p', { class: 'cpr-meta' }, [
      'Rules and the Vendor Guide are worked out from what is on file (active and draft products; "n/a" counts as done). ',
      prog ? 'Build-out steps from Catalog-Bot\'s tracker, last reported ' + ageText(Date.parse(prog.at)) + '.' : 'Catalog-Bot has not reported its build-out steps yet; they appear after its next tracker run.',
    ]));
    if (state.flash) { top.appendChild(el('p', { class: 'cpr-flash', role: 'status', text: state.flash })); state.flash = null; }
    var head = el('tr', null, [el('th', { scope: 'col', text: 'Brand' }), el('th', { scope: 'col', text: 'Products done' }), el('th', { scope: 'col', text: 'Vendor Guide' })]
      .concat(stages.map(function (s) { return el('th', { scope: 'col', class: 'cpr-stagecol', text: s }); }))
      .concat([el('th', { scope: 'col', text: 'Build-out' })]));
    var body = el('tbody');
    d.brands.forEach(function (br) {
      var open = state.brandOpen === br.brand;
      var stageById = {};
      ((br.progress && br.progress.stages) || []).forEach(function (s) { stageById[s.id] = s; });
      var tr = el('tr', { class: 'cpr-rowlink' + (open ? ' is-open' : '') + (br.complete ? ' cpr-brand--done' : '') + (br.parent ? ' cpr-brand--child' : ''), tabindex: '0', 'aria-expanded': open ? 'true' : 'false' }, [
        el('th', { scope: 'row', class: 'cpr-prod' }, [
          el('span', { class: 'cpr-prod__t', text: (br.parent ? '↳ ' : '') + br.brand }),
          el('span', { class: 'cpr-prod__h', text: brandSubline(br) }),
          br.progress && br.progress.notes ? el('span', { class: 'cpr-prod__note', text: br.progress.notes }) : null,
        ]),
        el('td', { text: br.trackerOnly ? '-' : fraction(br.productsComplete, br.products, br.productsComplete === br.products && br.products > 0) }),
        el('td', { text: br.guide ? fraction(br.guide.done, br.guide.needed, br.guide.complete) : '-' }),
      ].concat(stages.map(function (s) {
        if (!br.progress || !br.progress.reported) return el('td', { class: 'cpr-stage cpr-stage--none', text: '-' });
        var c = stageCell(stageById[s]);
        return el('td', { class: 'cpr-stage ' + c.cls, text: c.text, title: s });
      })).concat([el('td', { text: br.progress && br.progress.reported ? fraction(br.progress.done, br.progress.needed, br.progress.needed > 0 && br.progress.done === br.progress.needed) : 'not in the tracker' })]));
      var toggle = function () { state.brandOpen = open ? null : br.brand; renderBrandsView(); };
      tr.addEventListener('click', toggle);
      tr.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
      body.appendChild(tr);
      if (open) body.appendChild(el('tr', { class: 'cpr-detailrow' }, [el('td', { colspan: String(4 + stages.length) }, [brandDetail(br)])]));
    });
    root.appendChild(el('div', { class: 'cpr-tablewrap' }, [el('table', { class: 'cpr-table cpr-table--small' }, [el('thead', null, [head]), body])]));
    root.appendChild(brandsLegend());
  }

  /* Session 10 (Ryan 2026-10-11): the Excel retires only once this view shows what the sheet shows - the status and
     product lines on every brand, the tracker's note, and what each mark means. */
  var STATUS_WORDS = { live: 'Live', in_progress: 'In progress', pipeline: 'Pipeline', hold: 'On hold' };

  /** The line under a brand's name: supplier (or why there is none), product lines, status. Pure. */
  function brandSubline(br) {
    var p = br.progress || {};
    var parts = [br.trackerOnly ? 'No products in the store yet' : (br.supplier.name || 'not in the Vendor Guide')];
    if (p.lines) parts.push(p.lines);
    if (p.status) parts.push(STATUS_WORDS[p.status] || String(p.status).replace(/_/g, ' '));
    return parts.join(' · ');
  }

  function brandsLegend() {
    function item(mark, words) { return el('span', { class: 'cpr-legend__item' }, [el('strong', { text: mark }), ' ' + words]); }
    return el('div', { class: 'cpr-legend' }, [
      el('span', { class: 'cpr-legend__item', text: 'Stages:' }),
      item('✓', 'every step done or n/a'), item('3/5', 'steps done'), item('⌛', 'only steps planned for later'), item('-', 'not reported'),
      el('span', { class: 'cpr-legend__item', text: 'Steps:' }),
      item('✅', 'done'), item('🔄', 'in progress'), item('⏳', 'not started'), item('❌', 'blocked'), item('N/A', 'does not apply'),
      item('⌛', 'planned for later'), item('❓', 'needs Ryan'),
      el('span', { class: 'cpr-legend__item', text: 'Click a brand for every step and its full note.' }),
    ]);
  }

  function naButton(label, body, marked) {
    return el('button', { class: 'cpr-linkbtn', type: 'button', text: marked ? 'Count it again' : 'Mark n/a', onclick: function (e) {
      e.stopPropagation();
      var reason = '';
      if (!marked) {
        reason = window.prompt('Why does "' + label + '" not apply here? (8+ characters)', '') || '';
        if (reason.trim().length < 8) return;
      }
      api().post('/product-rules/na', Object.assign({}, body, { na: !marked, reason: reason.trim() })).then(function (r) {
        state.flash = (r && r.words) || 'Saved.';
        reloadBrands();
      }).catch(function (err) { window.alert('Not saved: ' + errText(err)); });
    } });
  }

  var MARK_WORDS = { done: 'Done', progress: 'In progress', pending: 'Not started', blocked: 'Blocked', na: 'n/a', deferred: 'Planned for later', ask: 'Needs Ryan' };

  function brandDetail(br) {
    var box = el('div', { class: 'cpr-branddetail' });
    if (!state.brandDetail || state.brandDetail.brand !== br.brand) {
      state.brandDetail = { brand: br.brand, loading: true };
      api().get('/product-rules/brands?brand=' + encodeURIComponent(br.brand)).then(function (r) {
        state.brandDetail = { brand: br.brand, row: (r.brands || [])[0] || null }; if (state.view === 'brands') renderBrandsView();
      }).catch(function (err) { state.brandDetail = { brand: br.brand, error: errText(err) }; if (state.view === 'brands') renderBrandsView(); });
    }
    var det = state.brandDetail.row;
    /* Session 10: the tracker's note, all of it (the list carries only its start). */
    var fullNote = (det && det.progress && det.progress.notes) || (br.progress && !br.progress.notesCut && br.progress.notes) || '';
    if (fullNote) {
      box.appendChild(el('p', { class: 'cpr-panel__t', text: 'Notes (from Catalog-Bot\'s tracker)' }));
      box.appendChild(el('p', { class: 'cpr-notefull', text: fullNote }));
    }
    if (!br.trackerOnly) {
      box.appendChild(el('div', { class: 'cpr-row cpr-row--start' }, [
        el('button', { class: 'cpr-btn cpr-btn--ghost', type: 'button', text: 'See ' + br.brand + ' products', onclick: function (e) {
          e.stopPropagation();
          state.supplier = br.brand; state.status = 'all'; state.area = CHECKLIST_AREA; state.gap = ''; state.guideFilter = ''; state.shown = PAGE_SIZE;
          setView('products');
        } }),
      ]));
      box.appendChild(el('p', { class: 'cpr-panel__t', text: 'Products' }));
      box.appendChild(el('table', { class: 'cpr-kv' }, [el('tbody', null, br.rules.map(function (r) {
        var missing = det && det.missing && det.missing[r.id] ? det.missing[r.id] : [];
        return el('tr', null, [
          el('th', { scope: 'row', text: r.label }),
          el('td', { text: fraction(r.done, r.needed, r.complete, r.na) + (r.unknown ? ' (' + r.unknown + ' not counted until the next rebuild)' : '') }),
          el('td', { text: r.na ? 'n/a: ' + r.na.reason + ' (' + (r.na.by || '?') + ')' : (missing.length ? 'Missing: ' + missing.slice(0, 8).map(function (m) { return m.title; }).join(', ') + (missing.length > 8 ? ' and ' + (missing.length - 8) + ' more' : '') : '') }),
          el('td', null, [naButton(r.label, { brand: br.brand, rule: r.id }, !!r.na)]),
        ]);
      }))]));
    }
    if (br.guide) {
      box.appendChild(el('p', { class: 'cpr-panel__t', text: 'Vendor Guide' + (br.supplier.name ? ' (' + br.supplier.name + ')' : '') }));
      var bySection = {};
      br.guide.items.filter(function (i) { return i.applies; }).forEach(function (i) { (bySection[i.section] = bySection[i.section] || []).push(i); });
      (state.brands.data.guideSections || []).forEach(function (sec) {
        var items = bySection[sec.id];
        if (!items) return;
        box.appendChild(el('p', { class: 'cpr-muted', text: sec.label }));
        box.appendChild(el('table', { class: 'cpr-kv' }, [el('tbody', null, items.map(function (i) {
          return el('tr', null, [
            el('th', { scope: 'row', text: i.label }),
            el('td', { text: i.na ? 'n/a' : (i.done ? '✓' : '✗') }),
            el('td', { text: i.na ? i.na.reason : (i.done ? '' : (i.why || i.where || '')) }),
            el('td', null, [br.supplier.guideId && i.id !== 'in-guide' ? naButton(i.label, { guideId: br.supplier.guideId, item: i.id }, !!i.na) : null]),
          ]);
        }))]));
      });
    }
    /* Session 9: Catalog-Bot's build-out steps, grouped by stage. A step the portal works out ("from what is on file")
       has no n/a; a done step needs none. */
    box.appendChild(el('p', { class: 'cpr-panel__t', text: 'Build-out steps (Catalog-Bot)' }));
    var steps = det && det.progress && det.progress.steps;
    if (!det || !det.progress || !det.progress.reported) {
      box.appendChild(el('p', { class: 'cpr-muted', text: state.brandDetail.loading ? 'Loading…' : 'This brand is not in Catalog-Bot\'s tracker yet.' }));
    } else {
      var byStage = {};
      steps.forEach(function (s) { (byStage[s.group] = byStage[s.group] || []).push(s); });
      det.progress.stages.forEach(function (st) {
        var c = stageCell(st);
        box.appendChild(el('p', { class: 'cpr-muted' }, [st.id + ' ', el('span', { class: 'cpr-stage ' + c.cls, text: c.text })]));
        box.appendChild(el('table', { class: 'cpr-kv' }, [el('tbody', null, (byStage[st.id] || []).map(function (s) {
          var live = s.source === 'portal' && !s.na;
          return el('tr', null, [
            el('th', { scope: 'row', text: s.id, title: s.desc || '' }),
            el('td', { class: 'cpr-mark cpr-mark--' + s.kind, text: s.mark || '-' }),
            el('td', { text: s.na ? 'n/a: ' + s.na.reason + ' (' + (s.na.by || '?') + ')' : (live ? 'Worked out from what is on file' : (MARK_WORDS[s.kind] || '')) }),
            el('td', null, [s.na ? naButton(s.id, { brand: br.brand, step: s.id }, true) : (live || s.kind === 'done' || s.kind === 'na' ? null : naButton(s.id, { brand: br.brand, step: s.id }, false))]),
          ]);
        }))]));
      });
    }
    if (state.brandDetail.error) box.appendChild(el('p', { class: 'cpr-meta cpr-meta--bad', text: 'Could not load this brand\'s detail: ' + state.brandDetail.error }));
    return box;
  }

  // ── Boot ────────────────────────────────────────────────────────────────────────────────────

  var booted = false;
  function boot() {
    if (booted) return;
    root = document.getElementById('product-rules-root');
    if (!root || !api()) return;
    var sess = null;
    try { sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || 'null'); } catch (e) { sess = null; }
    if (!sess) return; // still on the sign-in screen; `rep-portal:authenticated` retries
    booted = true;
    if (!sess.admin) {
      clear(root);
      root.appendChild(notice('error', 'Admins only', 'Product Rules carries costs and supplier rates, so only admins can open it.'));
      return;
    }
    load();
  }

  window.ProductRules = { _pure: { norm: norm, cellFor: cellFor, searchKey: searchKey, filterRows: filterRows, gapCounts: gapCounts, ageText: ageText, parseHash: parseHash, parseGuideHash: parseGuideHash, vendorsForGuide: vendorsForGuide, editValue: editValue, productEditValue: productEditValue, tickedHandles: tickedHandles, fraction: fraction, stageCell: stageCell, checkCell: checkCell, parseViewHash: parseViewHash, accCell: accCell } };

  if (typeof document !== 'undefined' && document.addEventListener) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
    window.addEventListener('rep-portal:authenticated', boot);
  }
})();
/* END CUSTOM_PRODUCTRULES_BY_CLAUDE */
